import React, { useEffect, useMemo, useState } from "react";
import {
  Building,
  Plus,
  Search,
  Eye,
  Users,
  UserPlus,
  X,
  Shield,
  Trash2,
} from "lucide-react";

import LoadingSpinner from "../../common/LoadingSpinner";
import { projectsAPI, usersAPI } from "../../../services/api";
import { PROJECT_ROLES } from "../../../constants/projectRoles";
import { isSystemAdmin } from "../../../constants/systemRoles";
import { isQaQcManager } from "../../../constants/projectRoles";
const toErrorMessage = (e) => {
  const detail = e?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((d) => `${(d.loc || []).join(".")}: ${d.msg}`)
      .join(" | ");
  }
  if (typeof detail === "string") return detail;

  return e?.message || "Request failed";
};

const normalizeStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .trim();

const getStatusColor = (status) => {
  const s = normalizeStatus(status);
  if (s === "active") return "bg-green-100 text-green-800";
  if (s === "completed") return "bg-blue-100 text-blue-800";
  if (s === "on_hold" || s === "on-hold")
    return "bg-yellow-100 text-yellow-800";
  if (s === "cancelled" || s === "canceled") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-800";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const ProjectsListView = ({
  onSelectProject,
  onCreateProject,
  currentUser,
  refreshKey = 0,
}) => {
  const isAdmin = isSystemAdmin(currentUser?.role);

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [teamOpen, setTeamOpen] = useState(false);
  const [teamProject, setTeamProject] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);

  const [allUsers, setAllUsers] = useState([]);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("project_manager");
  const [adding, setAdding] = useState(false);

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const wantedStatus = normalizeStatus(statusFilter);

    return (projects || []).filter((p) => {
      if (q) {
        const hay =
          `${p.name || ""} ${p.client || ""} ${p.project_number || ""} ${p.location || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (wantedStatus !== "all") {
        const pStatus = normalizeStatus(p.status);
        if (wantedStatus === "on-hold" && pStatus === "on_hold") return true;
        if (pStatus !== wantedStatus && `${pStatus}` !== `${wantedStatus}`)
          return false;
      }

      return true;
    });
  }, [projects, searchTerm, statusFilter]);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const getAllFn =
        projectsAPI.getAll || projectsAPI.list || projectsAPI.getProjects;

      if (!getAllFn) {
        throw new Error(
          "projectsAPI is missing getAll/list/getProjects export",
        );
      }

      const res = await getAllFn();
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail || e.message || "Failed to load projects",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersAPI.list();
      setAllUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.warn("Failed to load users list:", e);
      setAllUsers([]);
    }
  };

  const loadTeam = async (projectId) => {
    setTeamLoading(true);
    setTeamError(null);
    try {
      const res = await projectsAPI.getTeam(projectId);
      setTeamMembers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setTeamError(toErrorMessage(e));
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setIsLoading(false);
      return;
    }
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, refreshKey]);

  const handleViewProject = (project) => {
    if (typeof onSelectProject === "function") onSelectProject(project);
  };

  const openTeamModal = async (project) => {
    setTeamProject(project);
    setTeamOpen(true);
    setAddUserId("");
    setAddRole("project_manager");
    await Promise.all([loadTeam(project.id), loadUsers()]);
  };

  const closeTeamModal = () => {
    setTeamOpen(false);
    setTeamProject(null);
    setTeamMembers([]);
    setTeamError(null);
  };

  const getUserLabel = (userId) => {
    const u = allUsers.find((x) => x.id === userId);
    if (!u) return userId;
    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
    return name ? `${name} (${u.email})` : u.email || userId;
  };

  const getRoleLabel = (roleValue) =>
    PROJECT_ROLES.find((r) => r.value === roleValue)?.label || roleValue;

  const currentPM = useMemo(() => {
    return (teamMembers || []).find(
      (m) => m.is_active !== false && m.role === "project_manager",
    );
  }, [teamMembers]);

  const handleAddMember = async () => {
    if (!teamProject?.id) return;
    if (!addUserId) {
      setTeamError("Select a user (or enter a user id) to assign.");
      return;
    }

    setAdding(true);
    setTeamError(null);
    try {
      await projectsAPI.addTeamMember(teamProject.id, {
        user_id: Number(addUserId),
        role: addRole,
        //is_active: true,
      });
      await loadTeam(teamProject.id);
      setAddUserId("");
    } catch (e) {
      setTeamError(toErrorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  const handleDeactivate = async (memberId) => {
    if (!teamProject?.id) return;
    if (
      !window.confirm(
        "Remove this user from the project (deactivate membership)?",
      )
    )
      return;

    try {
      await projectsAPI.deactivateTeamMember(teamProject.id, memberId);
      await loadTeam(teamProject.id);
    } catch (e) {
      setTeamError(toErrorMessage(e));
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center">
        <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Admin Only</h3>
        <p className="text-gray-600">
          The Global Projects management view is available only to system
          administrators.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Projects</h2>
          <p className="text-gray-600">
            Create projects and assign Project Managers
          </p>
        </div>

        <button
          onClick={() =>
            typeof onCreateProject === "function" ? onCreateProject() : null
          }
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Project
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects (name, client, number, location)..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <button
            onClick={loadProjects}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {project.name}
                    </h3>
                    <p className="text-sm text-gray-600">{project.client}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}
                  >
                    {String(project.status || "—")}
                  </span>
                </div>

                <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                  {project.description || "—"}
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-gray-500">Location</p>
                    <p className="font-medium">{project.location}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Budget</p>
                    <p className="font-medium">
                      {project.budget
                        ? `$${(project.budget / 1000000).toFixed(1)}M`
                        : "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between text-sm text-gray-500">
                  <span>Start: {formatDate(project.start_date)}</span>
                  <span>End: {formatDate(project.end_date)}</span>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => openTeamModal(project)}
                  className="text-gray-700 hover:text-gray-900 text-sm font-medium flex items-center"
                  title="Assign PM / manage team"
                >
                  <Users className="w-4 h-4 mr-1" />
                  Team
                </button>

                <button
                  onClick={() => handleViewProject(project)}
                  className="text-orange-600 hover:text-orange-800 text-sm font-medium flex items-center"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <Building className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            No Projects Found
          </h3>
          <p className="text-gray-600 mb-4">
            Create your first project to get started
          </p>
          <button
            onClick={() =>
              typeof onCreateProject === "function" ? onCreateProject() : null
            }
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Create Project
          </button>
        </div>
      )}

      {/* TEAM MODAL */}
      {teamOpen && teamProject && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Project Team
                </h3>
                <p className="text-sm text-gray-600">
                  {teamProject.name} • {teamProject.project_number || "—"}
                </p>
              </div>
              <button
                onClick={closeTeamModal}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {teamError && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">
                  {teamError}
                </div>
              )}

              <div className="bg-gray-50 border rounded-lg p-4">
                <p className="text-sm text-gray-500">Current Project Manager</p>
                <p className="font-semibold text-gray-800">
                  {currentPM ? getUserLabel(currentPM.user_id) : "Not assigned"}
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 flex items-center">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign user to this project
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div className="md:col-span-2">
                    {allUsers.length > 0 ? (
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                      >
                        <option value="">Select user…</option>
                        {allUsers
                          .filter((u) => u.is_active !== false)
                          .map((u) => (
                            <option key={u.id} value={u.id}>
                              {`${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                                u.email}{" "}
                              ({u.email})
                            </option>
                          ))}
                      </select>
                    ) : (
                      <input
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="User ID (could not load users list)"
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                      />
                    )}
                  </div>

                  <div>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      value={addRole}
                      onChange={(e) => setAddRole(e.target.value)}
                    >
                      {PROJECT_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleAddMember}
                    disabled={adding}
                    className={`px-4 py-2 rounded-lg text-white flex items-center ${
                      adding
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-orange-500 hover:bg-orange-600"
                    }`}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    {adding ? "Assigning…" : "Assign"}
                  </button>
                </div>

                <p className="mt-2 text-xs text-gray-500">
                  Tip: assigning a user as <b>Project Manager</b> makes the
                  project appear in their Dashboard and enables them to manage
                  the project team.
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <h4 className="font-semibold text-gray-800">Team Members</h4>
                  <button
                    onClick={() => loadTeam(teamProject.id)}
                    className="px-3 py-1 text-sm border rounded-lg hover:bg-white"
                    disabled={teamLoading}
                  >
                    Refresh
                  </button>
                </div>

                {teamLoading ? (
                  <div className="p-4">
                    <LoadingSpinner />
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="p-4 text-sm text-gray-600">
                    No team members yet. Assign the Project Manager first.
                  </div>
                ) : (
                  <div className="divide-y">
                    {teamMembers.map((m) => (
                      <div
                        key={m.id}
                        className="px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {getUserLabel(m.user_id)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getRoleLabel(m.role)} • Assigned{" "}
                            {formatDate(m.assigned_at)}
                            {m.is_active === false ? " • (inactive)" : ""}
                          </p>
                        </div>

                        <button
                          onClick={() => handleDeactivate(m.id)}
                          className="p-2 rounded-lg hover:bg-gray-100"
                          title="Deactivate member"
                        >
                          <Trash2 className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-end">
              <button
                onClick={closeTeamModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsListView;
